import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Option } from '@sapphire/result';

import { SurveyChildKey } from '../../state/survey-creation-shared-state.model';
import { ACTIVE_CHILD_KEY_STATE } from '../../state/core/utils/active-child-navigation.helper';

/**
 * The child the current navigation asked to open, passed as
 * `ACTIVE_CHILD_KEY_STATE` (see activeChildNavigationExtras). For code that
 * runs while the navigation is in flight, such as a resolver.
 */
export function navigationActiveChildKey(
  router: Router,
): Option<SurveyChildKey> {
  return Option.from(router.currentNavigation()).andThen((navigation) =>
    activeChildKeyIn(navigation.extras.state),
  );
}

/**
 * The same child, read from the history entry the router has committed. For a
 * page created after its navigation ended: entering a new layout activates the
 * page from its outlet's `ngOnInit`, by which time `currentNavigation` is gone.
 */
export function locationActiveChildKey(
  location: Location,
): Option<SurveyChildKey> {
  return activeChildKeyIn(location.getState());
}

/**
 * The committed child as a survey id: only a project child's numeric key names
 * a survey the Manage Survey page can preview.
 */
export function locationActiveChildSurveyId(
  location: Location,
): Option<number> {
  return locationActiveChildKey(location).filter(
    (key): key is number => typeof key === 'number',
  );
}

// Navigation state is untyped, so anything but a number or string is ignored.
function activeChildKeyIn(state: unknown): Option<SurveyChildKey> {
  return Option.from(state)
    .filter((value): value is object => typeof value === 'object')
    .andThen((value) =>
      Option.from<unknown>(Reflect.get(value, ACTIVE_CHILD_KEY_STATE)),
    )
    .filter(isSurveyChildKey);
}

function isSurveyChildKey(key: unknown): key is SurveyChildKey {
  return typeof key === 'number' || typeof key === 'string';
}
